import styled from '@emotion/styled';
import {
  IluMatematica,
  LeadRegisterPage01,
  LeadRegisterPage02,
  LeadRegisterPage03,
  LeadRegisterPage04,
  LeadRegisterPage05,
} from '@innovamat/glimmer-assets';
import { useEffect, useRef, useState } from 'react';

const images = [
  LeadRegisterPage01,
  LeadRegisterPage02,
  LeadRegisterPage03,
  LeadRegisterPage04,
  LeadRegisterPage05,
];

const ImageSliderContainer = styled.div`
  height: 100vh;
  overflow: hidden;
  position: relative;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;

  @media (max-width: 1200px) {
    display: none;
  }
`;

const ImageWrapper = styled.div<{ isActive: boolean }>`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  opacity: ${({ isActive }) => (isActive ? 1 : 0)};
  transition: opacity 1s ease-in-out;
  display: flex;
  align-items: center;
  justify-content: center;

  img {
    height: 100%;
    width: auto;
    object-fit: contain;
    object-position: center;
  }
`;

const OverImageWrapper = styled.div`
  position: fixed;
  bottom: 0;
  right: 30%;
  pointer-events: none;
`;

type Image = {
  src: string;
};

function ImageSlider(): JSX.Element {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [containerWidth, setContainerWidth] = useState<number>(400);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentImageIndex((prevIndex) =>
        prevIndex === images.length - 1 ? 0 : prevIndex + 1
      );
    }, 5000);

    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const calculateOptimalWidth = () => {
      if (containerRef.current) {
        const containerHeight = containerRef.current.clientHeight;
        const currentImage = images[currentImageIndex] as unknown as Image;

        const img = new Image();
        img.onload = () => {
          const aspectRatio = img.width / img.height;
          const viewportWidth = window.innerWidth;
          const viewportHeight = window.innerHeight;

          // Calcular el ancho basado en la altura del contenedor y el aspect ratio
          const calculatedWidth = containerHeight * aspectRatio;

          // Calcular el ancho máximo que permite ver la imagen completa
          // Considerando que el slider ocupa la mitad derecha de la pantalla
          const availableWidth = viewportWidth * 0.5;
          const availableHeight = viewportHeight;

          // Calcular qué dimensión es la limitante
          const maxWidthByHeight = availableHeight * aspectRatio;
          const maxHeightByWidth = availableWidth / aspectRatio;

          let optimalWidth;

          if (maxHeightByWidth <= availableHeight) {
            // El ancho es la limitante, usar toda la altura disponible
            optimalWidth = availableWidth;
          } else {
            // La altura es la limitante, calcular el ancho basado en la altura
            optimalWidth = Math.min(calculatedWidth, maxWidthByHeight);
          }

          // Asegurar un ancho mínimo para usabilidad
          const minWidth = 300;
          optimalWidth = Math.max(optimalWidth, minWidth);

          setContainerWidth(optimalWidth);
        };
        img.src = currentImage.src;
      }
    };

    calculateOptimalWidth();

    const handleResize = () => {
      calculateOptimalWidth();
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [currentImageIndex]);

  return (
    <ImageSliderContainer
      ref={containerRef}
      style={{ width: `${containerWidth}px` }}
    >
      {images.map((image, index) => (
        <ImageWrapper key={index} isActive={index === currentImageIndex}>
          <img
            src={(image as unknown as Image).src}
            alt={`Lead Register Page ${index + 1}`}
          />
        </ImageWrapper>
      ))}
      <OverImageWrapper>
        <IluMatematica />
      </OverImageWrapper>
    </ImageSliderContainer>
  );
}

export { ImageSlider };
